/**
 * Top bar: nation, date & time (day/night), speed controls (host / single player only),
 * industry, manpower, research rate, and the victory-point race with the decisive-share marker.
 */
import { dayOf, hourOfDay, isNight, NO_HEX, Terrain, VP_HOLD_HOURS } from '@friction/engine';
import type { GameSpeed } from '@friction/protocol';
import { useMemo } from 'react';
import { bus } from '../../game/bus';
import { cssColor, formatHour } from '../../game/format';
import { gameStore, useGame } from '../../game/store';
import { formatTemp, seasonLabel, skyAt, weatherEffects, windFrom } from '../../game/weather';
import { useT } from '../../i18n';
import { nationName, regionName } from '../../i18n/names';
import { useLobby } from '../../net/lobby';
import { cx } from '../common/controls';
import { Icon } from '../common/Icon';
import { canControlSpeed } from './hotkeys';
import s from './hud.module.css';
import { hudStore, useHud } from './hudStore';
import { useCameraHex } from './useCameraHex';

const SPEED_LABEL = ['I', 'II', 'III', 'IV', 'V'];

function Clock() {
  const t = useT();
  const tick = useGame((g) => g.view?.tick ?? 0);
  const dayLimit = useGame((g) => g.config?.dayLimit ?? 0);
  const night = isNight(tick);
  const hour = hourOfDay(tick);
  return (
    <div className={s.clock} title={night ? t('top.night') : t('top.daylight')}>
      <span className={cx(s.clockGlyph, night ? s.night : s.day)}>
        <Icon name={night ? 'moon' : 'sun'} size={17} />
      </span>
      <span className={s.clockText}>
        <span className={s.clockDay}>
          {dayLimit > 0
            ? t('time.dayOf', { n: dayOf(tick), of: dayLimit })
            : t('time.day', { n: dayOf(tick) })}
        </span>
        <span className={s.clockTime}>{formatHour(tick)}</span>
      </span>
      <span className={s.dial} aria-hidden>
        <span className={s.needle} style={{ left: `${((hour + 0.5) / 24) * 100}%` }} />
      </span>
    </div>
  );
}

/**
 * The sky over the part of the theatre the map is showing: glyph, word, temperature and ground.
 * Click (or W) opens the Meteorological Service.
 */
function Weather() {
  const t = useT();
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  const open = useHud((h) => h.panel === 'weather');
  const camHex = useCameraHex();
  if (!view || !map || !grid) return null;
  const capital = map.cities[view.me.capitalCity]?.hex ?? 0;
  const hex = camHex !== NO_HEX && camHex < grid.count ? camHex : capital;
  const sky = skyAt(view, map, grid, hex, t.locale);
  const regionEn = map.region[hex] >= 0 ? map.regions[map.region[hex]]?.name : null;
  const effects = weatherEffects(sky.cond, map.terrain[hex] === Terrain.Marsh, t.locale);
  // German keeps nouns capitalised; English lower-cases the word inside the sentence.
  const lower = (w: string) => (t.locale === 'en' ? w.toLowerCase() : w);
  const tip = [
    t('top.wx.over', {
      season: seasonLabel(view.weather.season, t.locale),
      region: regionEn ? regionName(regionEn, t.locale) : t('top.wx.mapCentre'),
      word: lower(sky.word),
      temp: formatTemp(sky.temp, t.locale),
    }),
    t('top.wx.ground', { ground: sky.ground ? lower(sky.ground) : t('ground.firm') }),
    t('wind.fromCap', { dir: windFrom(view.weather.wind) }),
    ...(effects.length ? ['', ...effects] : []),
    '',
    t('top.wx.met'),
  ].join('\n');
  return (
    <button
      type="button"
      className={cx(s.wx, open && s.wxOn)}
      title={tip}
      onClick={() => {
        bus.emit('sound', { name: 'paper' });
        hudStore.getState().togglePanel('weather');
      }}
    >
      <span className={cx(s.clockGlyph, s.wxGlyph, s[`wx_${sky.icon}`])}>
        <Icon name={sky.icon} size={18} />
      </span>
      <span className={s.clockText}>
        <span className={s.clockDay}>{sky.word}</span>
        <span className={s.wxLine}>
          {formatTemp(sky.temp, t.locale)}
          {sky.ground && <span className={s.wxGround}> · {sky.ground}</span>}
        </span>
      </span>
    </button>
  );
}

/** Pause and speeds I–V — live for single player and the multiplayer host (also while observing). */
export function Speed() {
  const t = useT();
  const speed = useGame((g) => g.speed);
  const paused = useGame((g) => g.paused);
  // Re-evaluated when the room changes (host migration).
  useLobby((l) => l.room?.hostId);
  const allowed = canControlSpeed();
  const g = gameStore.getState();
  const why = allowed ? undefined : t('top.hostClock');
  return (
    <div className={s.speed} title={why} data-tut="speed">
      <button
        type="button"
        className={cx(s.speedBtn, s.pauseBtn, paused && s.pauseOn)}
        disabled={!allowed}
        title={allowed ? (paused ? t('top.resume') : t('top.pause')) : why}
        onClick={() => {
          bus.emit('sound', { name: 'click' });
          g.setPaused(!paused);
        }}
      >
        <Icon name={paused ? 'play' : 'pause'} size={14} />
      </button>
      {SPEED_LABEL.map((label, i) => {
        const v = (i + 1) as GameSpeed;
        return (
          <button
            key={v}
            type="button"
            className={cx(s.speedBtn, !paused && speed === v && s.speedOn)}
            disabled={!allowed}
            title={allowed ? t('top.speed', { label, v }) : why}
            onClick={() => {
              bus.emit('sound', { name: 'click' });
              g.setSpeed(v);
              if (paused) g.setPaused(false);
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Resource({
  icon,
  value,
  unit,
  label,
  title,
}: {
  icon: 'factory' | 'manpower' | 'research';
  value: string;
  unit: string;
  label: string;
  title: string;
}) {
  return (
    <div className={s.res} title={title}>
      <Icon name={icon} size={20} className={s.resIcon} />
      <span className={s.resText}>
        <span className={s.resValue}>
          {value}
          <span className={s.resUnit}>{unit}</span>
        </span>
        <span className={s.resLabel}>{label}</span>
      </span>
    </div>
  );
}

function VpRace() {
  const t = useT();
  const factions = useGame((g) => g.view?.factions);
  const me = useGame((g) => g.faction);
  const map = useGame((g) => g.map);
  const share = useGame((g) => g.config?.vpVictoryShare ?? 0.6);
  const clock = useGame((g) => g.view?.victoryClock ?? null);
  const tick = useGame((g) => g.view?.tick ?? 0);
  const total = useMemo(() => map?.cities.reduce((a, c) => a + c.vp, 0) ?? 0, [map]);
  if (!factions) return null;
  const sum = Math.max(
    total,
    factions.reduce((a, f) => a + f.vp, 0),
    1,
  );
  const myTeam = factions.find((f) => f.id === me)?.team ?? -1;
  // My team first, then the others grouped by team — adjacent segments read as a coalition.
  const ordered = [...factions].sort(
    (a, b) => (a.team === myTeam ? -1 : 0) - (b.team === myTeam ? -1 : 0) || a.team - b.team || a.id - b.id,
  );
  const mine = factions.find((f) => f.id === me);
  const teamVp = factions.filter((f) => f.team === myTeam).reduce((a, f) => a + f.vp, 0);
  const tip = ordered
    .map(
      (f) =>
        t('top.vp.row', { name: nationName(f, t.locale), vp: f.vp, pct: t.pct(f.vp / sum) }) +
        (f.alive ? '' : t('top.vp.capitulated')),
    )
    .join('\n');
  return (
    <div className={s.vp} data-tut="vp" title={`${tip}\n\n${t('top.vp.goal', { pct: t.pct(share), sum })}`}>
      <div className={s.vpHead}>
        {/* The countdown takes the label's place: the top bar has room for exactly two rows. */}
        {clock ? (
          <span className={cx(s.vpLabel, s.vpClock, clock.team === myTeam ? s.vpClockMine : s.vpClockFoe)}>
            {t(clock.team === myTeam ? 'top.vp.victoryIn' : 'top.vp.enemyVictoryIn', {
              h: Math.max(0, VP_HOLD_HOURS - (tick - clock.since)),
            })}
          </span>
        ) : (
          <span className={s.vpLabel}>{t('top.vp.label')}</span>
        )}
        <span className={s.vpMine}>
          {mine?.vp ?? 0}
          {teamVp !== (mine?.vp ?? 0) && t('top.vp.team', { vp: teamVp })}
          {t('top.vp.need', { sum, pct: t.pct(share) })}
        </span>
      </div>
      <div className={s.vpBar}>
        {ordered.map((f) =>
          f.vp > 0 ? (
            <span
              key={f.id}
              className={cx(s.vpSeg, f.id === me && s.vpSegMe)}
              style={{
                width: `${(f.vp / sum) * 100}%`,
                background: cssColor(f.color),
                opacity: f.alive ? 1 : 0.4,
              }}
            />
          ) : null,
        )}
        <span
          className={s.vpMark}
          style={{ left: `${share * 100}%` }}
          data-label={t.pct(share).replace(/\s/g, '')}
        />
      </div>
    </div>
  );
}

export function TopBar() {
  const t = useT();
  const me = useGame((g) => g.view?.me);
  if (!me) return null;
  return (
    <header className={s.top}>
      <div className={s.seg}>
        <span className={s.emblem} style={{ background: cssColor(me.color) }}>
          {me.adjective[0]}
        </span>
        <span className={s.faction}>
          <span className={s.factionName}>{nationName(me, t.locale)}</span>
          <span className={s.factionSub}>
            {t('top.generalStaff', { state: me.alive ? t('top.inField') : t('top.capitulated') })}
          </span>
        </span>
      </div>
      <div className={s.seg}>
        <Clock />
        <Weather />
      </div>
      <div className={s.seg}>
        <Speed />
      </div>
      <div className={s.seg}>
        <Resource
          icon="factory"
          value={t.num(me.industryRate, 1)}
          unit={t('top.perHour')}
          label={t('top.industry')}
          title={t('top.industry.hint')}
        />
        <Resource
          icon="manpower"
          value={String(Math.round(me.manpower))}
          unit={t('top.thousands')}
          label={t('top.manpower')}
          title={t('top.manpower.hint')}
        />
        <Resource
          icon="research"
          value={t.num(me.researchRate, 2)}
          unit={t('top.perHour')}
          label={t('top.research')}
          title={t('top.research.hint')}
        />
      </div>
      <div className={cx(s.seg, s.grow)}>
        <VpRace />
      </div>
      <div className={s.seg} style={{ paddingRight: 10 }}>
        <button
          type="button"
          className={s.menuBtn}
          title={t('top.menu')}
          onClick={() => {
            bus.emit('sound', { name: 'click' });
            hudStore.getState().setOverlay('menu');
          }}
        >
          <Icon name="menu" size={20} />
        </button>
      </div>
    </header>
  );
}
